/**
 * Endpoints #56-#66 — saved posts (SPEC `saved-posts`).
 *
 * A save is a **user-curated annotation layer over `posts`**: nothing is copied, every surface
 * renders live post data. A `saved_posts` row *is* "All Saved" — there is no system list — and
 * custom lists, tags and the note hang off it.
 *
 * Everything here is either a Zod schema (validated at the API edge) or a type inferred from one,
 * plus the caps and the closed colour vocabulary both sides key off (§10.4, §12.5 rule 5).
 */
import { z } from 'zod';
import { POSTS_SORT_KEYS } from '../columns.js';
import { SORT_DIRS } from '../enums.js';
import { IdListParamSchema, IdSchema, MAX_FEED_LIMIT, ViewFilterParamsSchema } from './common.js';
/* -------------------------------------------------------------------- caps */
export const SAVED_LIST_NAME_MAX = 60;
export const SAVED_TAG_NAME_MAX = 40;
export const SAVED_NOTE_MAX_CHARS = 2000;
export const SAVED_TAGS_PER_POST_MAX = 20;
export const SAVED_LISTS_MAX = 100;
export const SAVED_TAGS_MAX = 200;
/**
 * Chip colours a tag can carry — a **discriminant, not a hex** (§12.5 rule 5): the server assigns
 * one round-robin at creation and the client maps the token to its own CSS variables.
 */
export const SAVED_TAG_COLORS = [
    'stone',
    'amber',
    'sky',
    'emerald',
    'rose',
    'violet',
    'cyan',
    'lime',
];
/**
 * List names that collide with the save popup's pseudo-entry and the screen's umbrella label.
 * Compared case-insensitively, like the `COLLATE NOCASE` uniqueness of the column itself.
 */
export const RESERVED_SAVED_LIST_NAMES = ['saved', 'all saved'];
export function isReservedSavedListName(name) {
    const normalized = name.trim().toLowerCase();
    return RESERVED_SAVED_LIST_NAMES.some((reserved) => reserved === normalized);
}
/* ------------------------------------------------------------------ bodies */
const listName = z.string().trim().min(1).max(SAVED_LIST_NAME_MAX);
const tagName = z.string().trim().min(1).max(SAVED_TAG_NAME_MAX);
/** #57 `POST /api/saved/lists` */
export const CreateSavedListBodySchema = z.object({ name: listName });
/** #58 `PATCH /api/saved/lists/:id` */
export const UpdateSavedListBodySchema = z.object({ name: listName });
/** #67 `POST /api/saved/tags` — the colour is assigned round-robin, never sent. */
export const CreateSavedTagBodySchema = z.object({ name: tagName });
/** #61 `PATCH /api/saved/tags/:id` — the colour is server-owned and deliberately not settable. */
export const UpdateSavedTagBodySchema = z.object({ name: tagName });
/**
 * #64 `PUT /api/posts/:id/saved` — the popup's one batch commit, **replace-all**.
 *
 * `tags` are names, not ids: the input creates as it resolves (NOCASE), which is what makes
 * "type a word, press Enter" a single round trip. `note: null` (or `''`) clears the note.
 */
export const PutSavedBodySchema = z.object({
    listIds: z.array(IdSchema).max(SAVED_LISTS_MAX).default([]),
    tags: z.array(tagName).max(SAVED_TAGS_PER_POST_MAX).default([]),
    note: z.string().max(SAVED_NOTE_MAX_CHARS).nullable().default(null),
});
/* ------------------------------------------------------------------ query */
/**
 * #66 `GET /api/saved/feed`
 *
 * The §5.2 filter set **minus `authorIds`** (a collection is not author-scoped) plus the saved-only
 * narrowings and — like #51 — the `groupIds`/`profileIds` scope pair, which is how the sidebar asks
 * for "saves by these people" without `authorIds` meaning two things. Two deliberate deviations from
 * §5.2, both because a collection defaults to showing itself: omitted `from`/`to` mean **all time**,
 * and the default sort is `savedAt` desc.
 *
 * `savedFrom`/`savedTo` bound `saved_posts.saved_at`; the range control still bounds
 * `published_at`, so the two are independent by construction.
 */
export const SavedFeedQuerySchema = ViewFilterParamsSchema.omit({ authorIds: true }).extend({
    /** Custom lists, any-of. Nothing checked = All Saved. */
    listIds: IdListParamSchema.optional(),
    /** Tags, any-of (OR) — the sidebar's stated semantics. */
    tagIds: IdListParamSchema.optional(),
    /**
     * Checked groups, resolved to their members **including archived ones** — the one deliberate
     * difference from #51's scope params: a collection outlives archiving (§6.6), so narrowing it by
     * a group must not silently drop the saves of a member who was archived since.
     */
    groupIds: IdListParamSchema.optional(),
    /** Individually checked profiles; unioned with the groups' members. */
    profileIds: IdListParamSchema.optional(),
    savedFrom: z.coerce.number().int().optional(),
    savedTo: z.coerce.number().int().optional(),
    sort: z.enum(POSTS_SORT_KEYS).default('savedAt'),
    dir: z.enum(SORT_DIRS).default('desc'),
    /** Opaque cursor returned as `nextCursor`; absent on the first page. */
    cursor: z.string().max(400).optional(),
    limit: z.coerce.number().int().positive().max(MAX_FEED_LIMIT).default(MAX_FEED_LIMIT),
});
//# sourceMappingURL=saved.js.map